import { useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { createColumnHelper } from '@tanstack/react-table';
import { LocalizedDateTimeFromUtc, Parenthesized } from '@mews-commander/ui';
import { UnicodeEntity } from '@mews/enums';
import { useResolveRoute } from '@mews/routing';
import { Bill } from '@mews-commander/data-access';
import { lt } from '@mews/framework';
import { RouteName } from '@mews-commander/enums';
import { Body, Link, Stack, StackItem } from '@optimus-web/core';
import { compactArray } from '@mews/fp';
import { AccountLink, CompanyName } from '../../../../../../components';
import { BillOwnerType } from '../BillOwnerType';
import { BillAccountCustomerLink } from '../BillAccountCustomerLink';
import { BillStatusCell } from '../BillStatusCell';
import { BillAccountCompanyLink } from '../BillAccountCompanyLink';
import { changeTab } from '../../../../actions';
import { CompanyDetailTabs } from '../../../../enums';
import { openModal } from '../../../../../app';

const columnHelper = createColumnHelper<Bill>();

export const useColumns = ({ billOwnerId }: { billOwnerId: string }) => {
    const dispatch = useDispatch();
    const resolveRoute = useResolveRoute();

    return useMemo(
        () =>
            compactArray([
                columnHelper.accessor('id', {
                    header: () => lt('Bill'),
                    cell: (props) => (
                        <Link
                            standalone
                            variant="basic"
                            onClick={() => {
                                dispatch(openModal(resolveRoute(RouteName.BillDetail, { id: props.row.original.id })));
                            }}
                        >
                            <Body whiteSpace="nowrap" size="small">
                                {props.row.original.name}
                            </Body>
                        </Link>
                    ),
                }),
                columnHelper.accessor('ownerId', {
                    header: () => lt('Owner'),
                    cell: (props) => <CompanyDetailsTabLink ownerId={props.getValue()} />,
                }),
                columnHelper.accessor('ownerId', {
                    id: 'ownerType',
                    header: () => lt('ProfileType'),
                    cell: (props) => <BillOwnerType ownerId={props.getValue()} />,
                }),
                columnHelper.accessor('ownerId', {
                    id: 'customer',
                    header: () => lt('Customer'),
                    cell: (props) => (
                        <Stack vertical>
                            <BillAccountCustomerLink accountId={props.getValue()} />
                            {props.row.original.associatedAccountId ? (
                                <>
                                    <BillAccountCustomerLink accountId={props.row.original.associatedAccountId} />
                                </>
                            ) : (
                                UnicodeEntity.Dash
                            )}
                        </Stack>
                    ),
                }),
                columnHelper.accessor('ownerId', {
                    id: 'company',
                    header: () => lt('Company'),
                    cell: (props) => {
                        const ownerId = props.getValue();
                        const { associatedAccountId } = props.row.original;

                        return (
                            <Stack vertical>
                                {ownerId === billOwnerId ? (
                                    <CompanyDetailsTabLink ownerId={ownerId} />
                                ) : (
                                    <BillAccountCompanyLink accountId={ownerId} />
                                )}
                                {associatedAccountId ? (
                                    <BillAccountCompanyLink accountId={associatedAccountId} />
                                ) : null}
                            </Stack>
                        );
                    },
                }),
                columnHelper.accessor('ownerTaxIdentifier', {
                    header: () => lt('OwnerTaxId'),
                }),
                columnHelper.accessor('closedUtc', {
                    header: () => lt('Issued'),
                    cell: (props) => {
                        const closedUtc = props.getValue();
                        const { closerProfileId } = props.row.original;

                        return closedUtc && closerProfileId ? (
                            <Stack wrap>
                                <StackItem>
                                    <LocalizedDateTimeFromUtc utcString={closedUtc} />
                                    {UnicodeEntity.NoBreakSpace}
                                </StackItem>
                                <StackItem>
                                    <Parenthesized>
                                        <AccountLink id={closerProfileId} />
                                    </Parenthesized>
                                </StackItem>
                            </Stack>
                        ) : (
                            UnicodeEntity.Dash
                        );
                    },
                }),
                columnHelper.accessor('dueUtc', {
                    header: () => lt('DueDate'),
                    cell: (props) => {
                        const dueUtc = props.getValue();

                        return dueUtc ? <LocalizedDateTimeFromUtc utcString={dueUtc} /> : UnicodeEntity.Dash;
                    },
                }),
                columnHelper.accessor('paidUtc', {
                    header: () => lt('Paid'),
                    cell: (props) => {
                        const paidUtc = props.getValue();

                        return paidUtc ? <LocalizedDateTimeFromUtc utcString={paidUtc} /> : UnicodeEntity.Dash;
                    },
                }),
                columnHelper.accessor('lastReminderDateUtc', {
                    header: () => lt('ReminderSent'),
                    cell: (props) => {
                        const { lastReminderDateUtc } = props.row.original;

                        return lastReminderDateUtc ? (
                            <LocalizedDateTimeFromUtc utcString={lastReminderDateUtc} />
                        ) : (
                            UnicodeEntity.Dash
                        );
                    },
                }),
                columnHelper.accessor('state', {
                    header: () => lt('Status'),
                    cell: (props) => <BillStatusCell bill={props.row.original} />,
                }),
                columnHelper.accessor('notes', {
                    header: () => lt('Notes'),
                    meta: { width: 300 },
                }),
            ]),
        [billOwnerId, dispatch, resolveRoute]
    );
};

const CompanyDetailsTabLink = ({ ownerId }: { ownerId: string }) => {
    const dispatch = useDispatch();

    return (
        <Link
            variant="basic"
            data-test-company-link={ownerId}
            onClick={() => dispatch(changeTab(CompanyDetailTabs.Details))}
        >
            <CompanyName id={ownerId} />
        </Link>
    );
};
